import React, { useEffect, useRef } from "react";
import "../../styles/esg_Checkpoint.css";

const ESG_CONFIG = {
  basePlaceholder:
    "Rollover the diagrams to discover what ESG means for your business",
  defaultInfoColor: "#555",
  rotationSpeed: 10, // deg per second
};

const vennText = {
  e:
    "Emissions show how much CO₂ your business produces.<br><br>" +
    "They’re the easiest ESG metric to track and the hardest to ignore.<br><br>" +
    "Get it wrong and you risk higher costs, fines, and reputational damage—even public <b>“cancellation.”</b>",
  s:
    "Society covers how your business impacts people—employees, customers, and communities.<br><br>" +
    "It’s hard to quantify, and a nightmare if you don’t already have the right policies and<br> processes in place.",
  g:
    "Governance is simply how you run things—who decides what, how money is handled, and <br> how you spot risks.<br><br>" +
    "If it’s weak, small problems get expensive fast—and banks, insurers, or investors<br> may suddenly start asking  "+
    "difficult questions and ultimately withdraw support if your<br> weakness affects their own reporting.",
};

const vennColors = {
  e: "rgb(255, 51, 51)", // red
  s: "rgb(213, 155, 50)", // ochre
  g: "rgb(91, 21, 158)", // purple
};

const Esg_Checkpoint = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const root = containerRef.current;
    const rotator = root.querySelector("#venn-rotator");
    const labels = root.querySelectorAll(".circle-label");
    const info = root.querySelector("#venn-info");
    const circles = root.querySelectorAll(".circle");

    if (!rotator || !info || circles.length === 0) return;

    // initial info text + colour
    info.innerHTML = ESG_CONFIG.basePlaceholder;
    info.style.backgroundColor = ESG_CONFIG.defaultInfoColor;

    // state kept in refs so it persists between animation frames
    let currentAngle = 0;
    let lastTime = null;
    let isPaused = false;
    const rotationSpeed = ESG_CONFIG.rotationSpeed;
    let frameId = null;

    const setBreathingPaused = (paused) => {
      circles.forEach((circle) => {
        circle.style.animationPlayState = paused ? "paused" : "running";
      });
    };

    const animate = (timestamp) => {
      if (lastTime === null) lastTime = timestamp;
      const deltaSeconds = (timestamp - lastTime) / 1000;
      lastTime = timestamp;

      if (!isPaused) {
        currentAngle += rotationSpeed * deltaSeconds;

        rotator.style.transform = `rotate(${currentAngle}deg)`;

        labels.forEach((label) => {
          label.style.transform = `rotate(${-currentAngle}deg)`;
        });
      }

      frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);

    const handleEnter = (circle) => {
      isPaused = true;
      setBreathingPaused(true);

      let key = null;
      if (circle.classList.contains("circle-e")) key = "e";
      if (circle.classList.contains("circle-s")) key = "s";
      if (circle.classList.contains("circle-g")) key = "g";

      if (key) {
        info.innerHTML = vennText[key];
        info.style.backgroundColor = vennColors[key];
      }
    };

    const handleLeave = () => {
      isPaused = false;
      setBreathingPaused(false);
      info.innerHTML = ESG_CONFIG.basePlaceholder;
      info.style.backgroundColor = ESG_CONFIG.defaultInfoColor;
    };

    circles.forEach((circle) => {
      circle.addEventListener("mouseenter", () => handleEnter(circle));
      circle.addEventListener("mouseleave", handleLeave);
    });

    // cleanup on unmount
    return () => {
      if (frameId) cancelAnimationFrame(frameId);
      circles.forEach((circle) => {
        circle.removeEventListener("mouseenter", () => handleEnter(circle));
        circle.removeEventListener("mouseleave", handleLeave);
      });
    };
  }, []);

  return (
    <div className="venn-container" ref={containerRef}>
      <div id="venn-rotator">
        <div className="esg-venn">
          <div className="circle circle-e">
            <span className="circle-label">E</span>
          </div>
          <div className="circle circle-s">
            <span className="circle-label">S</span>
          </div>
          <div className="circle circle-g">
            <span className="circle-label">G</span>
          </div>
        </div>
      </div>

      <div id="venn-info" />
    </div>
  );
};

export default Esg_Checkpoint;
